import { useTranslation } from '@fluxora/i18n';

import { isEmailProven } from '../services/email-verification.service';
import type { EmailOtpPhase } from '../services/email-verification.service';

/**
 * The "this address is proven" confirmation, as ONE tick inside the email field.
 *
 * ── WHY IT REPLACED A BADGE ────────────────────────────────────────────────
 * It used to be a full-width green `Badge` sitting under the input. At the input's
 * own width it read as an alert slot — the same shape and position this form uses for
 * FIELD ERRORS — so a success was rendered in the visual language of a problem, and it
 * pushed the SSN field and the consent checkboxes down the page every time someone
 * verified. A confirmation should be the smallest mark that still says "done".
 *
 * ── IT IS STILL ANNOUNCED ──────────────────────────────────────────────────
 * ⚠️ DROPPING THE VISIBLE TEXT MUST NOT DROP THE INFORMATION. The tick carries the
 * SAME `shop.credit.otp.verified` string as the badge did, as its accessible name
 * (`role="img"` + `aria-label`) and as a `title` for a sighted customer who hovers.
 * `CreditPage` additionally points the email input's `aria-describedby` at
 * `EMAIL_VERIFIED_TICK_ID`, so a screen reader landing on the field is told it is
 * verified without having to find the glyph. The "if you change it we'll send a new
 * code" note stays where it was, under the field — that is real information, and only
 * the pill was redundant.
 *
 * Renders `null` unless the phase is `verified`, which is what keeps
 * `aria-describedby` from ever pointing at an element that is not there.
 */

/** The id `CreditPage` wires the email input's `aria-describedby` to. */
export const EMAIL_VERIFIED_TICK_ID = 'cr-email-verified';

export interface EmailVerifiedTickProps {
  readonly phase: EmailOtpPhase;
}

export function EmailVerifiedTick({ phase }: EmailVerifiedTickProps): JSX.Element | null {
  const { t } = useTranslation();

  if (!isEmailProven(phase)) return null;

  const label = t('shop.credit.otp.verified');
  return (
    <span
      id={EMAIL_VERIFIED_TICK_ID}
      role="img"
      aria-label={label}
      title={label}
      // `pointer-events-none` so the tick never steals a click aimed at the field it
      // sits on top of. `text-savings` is the storefront's positive-outcome token —
      // the same green the price-saving figures use — so the mark is not a stray hex.
      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-savings"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="h-4 w-4"
      >
        <path d="m5 13 4 4L19 7" />
      </svg>
    </span>
  );
}
